import React, { useMemo, useState, CSSProperties, useEffect } from 'react';
import { t } from 'i18next';

import { fetchFactory, isApiError } from 'Libs/http/utils';
import SUploadDropArea from 'Components/structural/SUploadDropArea/SUploadDropArea';
import MModal from 'Components/modal/MModal/MModal';
import { COLORS } from 'Components/foundation';

import styles from './MFileUpload.style';

export type MFileUploadProps = {
  title: string;
  description: string;
  maxFileSize: number;
  allowedMimeTypes: Array<string>;
  format: Array<string>;
  uploadErrors: Record<string, string>;
  uploadUrl: string;
  visible: boolean;
  onClose: () => void;
  onEndUpload: <T>(result: T) => void;
  style?: CSSProperties;
};

const bytesToMegabytes = (bytes: number) => Math.round(bytes / (1024 * 1024));

export const MFileUpload = ({
  title,
  description,
  maxFileSize,
  allowedMimeTypes,
  format,
  visible,
  uploadErrors,
  uploadUrl,
  onClose,
  onEndUpload,
  style,
}: MFileUploadProps) => {
  const modalStyle = useMemo(() => ({ ...styles.modal, ...style }), [style]);
  const [isUploadInProgress, setIsUploadInProgress] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState('');
  const [additionalErrorText, setAdditionalErrorText] = useState('');
  const [abortController, setAbortController] = useState<AbortController | null>(null);

  const formatList = `${format.slice(0, -1).join(', ')} or ${format.slice(-1)}`;
  const defaultLabel = t('modal_components:m_file_upload.default_label', {
    formatList,
    maxFileSize: bytesToMegabytes(maxFileSize),
  });
  const warnSentence2 = t('modal_components:m_file_upload.warn_sentence2', { formatList });
  const errorIncorrectFormat2 = t('modal_components:m_file_upload.error_incorrect_format2', { formatList });

  const handleUpload = async () => {
    const formData = new FormData();

    if (!uploadedFile) return;

    formData.append('file', uploadedFile);

    setIsUploadInProgress(true);
    const { fetchFn, abortController: controller } = fetchFactory({
      route: uploadUrl,
      method: 'POST',
      payload: formData,
      multipart: true,
    });

    try {
      setAbortController(controller);

      const result = await fetchFn();

      onEndUpload(result);
    } catch (error) {
      if (!isApiError(error) || !error.errorCode) {
        console.error(error);
      } else {
        setUploadError(uploadErrors[error.errorCode] || error.message);
        if (error.params?.emails) {
          setAdditionalErrorText((error.params.emails as unknown as string[]).join(', '));
        }
      }
    } finally {
      setIsUploadInProgress(false);
      setUploadedFile(null);
    }
  };

  const handleAbortUpload = () => {
    abortController?.abort();
    setIsUploadInProgress(false);
  };

  const handleFileRemove = () => {
    if (isUploadInProgress) {
      handleAbortUpload();
    }

    setUploadedFile(null);
  };

  const handleDrop = (file: File) => {
    setUploadError('');
    setAdditionalErrorText('');
    setUploadedFile(file);
  };

  const handleClose = () => {
    handleFileRemove();
    setUploadError('');
    setAdditionalErrorText('');

    onClose();
  };

  useEffect(() => {
    return () => {
      abortController?.abort();
    };
  }, []);

  return (
    <MModal
      title={title}
      titleStyle={styles.title}
      description={description}
      descriptionStyle={styles.description}
      footerStyle={styles.footer}
      labelActionButton={t('modal_components:m_file_upload.label_action_button')}
      labelSecondButton={t('modal_components:m_file_upload.label_second_button')}
      onCloseModal={handleClose}
      onAction={handleUpload}
      onSecondAction={isUploadInProgress ? handleAbortUpload : handleClose}
      visible={visible}
      type="standard"
      showExitCross
      style={modalStyle}
      bodyStyle={styles.body}
      isActionLoading={isUploadInProgress}
      disableActionButton={!uploadedFile}
    >
      <div style={styles.contentWrapper}>
        <SUploadDropArea
          backgroundColor={COLORS.BLUE_GRAY_LIGHT.DEFAULT}
          dotsColor={COLORS.PRIMARY.DEFAULT}
          defaultSentence={t('modal_components:m_file_upload.default_sentence')}
          defaultLabel={defaultLabel}
          dropSentence={t('modal_components:m_file_upload.drop_sentence')}
          warnSentence1={t('modal_components:m_file_upload.warn_sentence')}
          warnSentence2={warnSentence2}
          errorIncorrectFormat1={t('modal_components:m_file_upload.error_incorrect_format1')}
          errorIncorrectFormat2={errorIncorrectFormat2}
          errorSizeTooBig={t('modal_components:m_file_upload.error_size_too_big')}
          errorManyFiles={t('modal_components:m_file_upload.error_many_files')}
          uploadingSentence=""
          uploadError={uploadError}
          errorAdditionalInfo={additionalErrorText}
          allowedMimeTypes={allowedMimeTypes}
          onDrop={handleDrop}
          maxFileSize={maxFileSize}
          fileName={uploadedFile?.name}
          removeFile={handleFileRemove}
        />
      </div>
    </MModal>
  );
};

export default MFileUpload;
